import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import Checklist from 'app/pages/loanApplication/tabs/checklists/Checklist';
import { documentsApi } from 'app/services/api';
import PropTypes from 'prop-types';

import { PAGE_CODES } from 'constants/pageCodes';
import { getFileDateTimeName } from 'utils/file';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import {
  selectApplicationChecklists,
  selectApplicationChecklistsFormattedValues,
} from 'store/loanApplications/selectors';

import { worksheetChecklistWrapperStyles } from '../../WorksheetDrawer.styles';

import { WorksheetChecklistPanelTestIds } from './WorksheetChecklistPanel.testIds';

const WorksheetChecklistPanel = ({ checklistId, isLoading = false }) => {
  const dispatch = useDispatch();

  const checklists = useSelector(selectApplicationChecklists);
  const checklistsResults = useSelector(selectApplicationChecklistsFormattedValues);
  const isDownloadAllowed = useSelector(
    selectIsClaimAllowed(PAGE_CODES.DOWNLOAD_AND_PRINT_DOCUMENT),
  );

  const matchingResults = useMemo(
    () => checklistsResults.filter(({ checklist }) => checklist?.id === checklistId),
    [checklistsResults, checklistId],
  );

  const onDownloadChecklist = useCallback(
    async e => {
      e.stopPropagation();

      try {
        const { id } = e.currentTarget.dataset;
        const { checklist, documentId, createdDate } = checklists[id];
        const fileName = getFileDateTimeName({
          fileName: checklist.name,
          date: createdDate,
          extension: 'pdf',
        });

        const { data: pdfBlob } = await documentsApi.getContent({ documentId });

        await downloadOrShareBlob(pdfBlob, fileName);
      } catch {
        dispatch(showMessageAction({ message: 'Could not download document', variant: 'error' }));
      }
    },
    [checklists, dispatch],
  );

  if (!checklistId || !matchingResults.length) {
    return null;
  }

  return (
    <Box data-testid={WorksheetChecklistPanelTestIds.PANEL} sx={worksheetChecklistWrapperStyles}>
      <Typography
        className="mb-12"
        component="div"
        data-testid={WorksheetChecklistPanelTestIds.TITLE}
        variant="bold16"
      >
        Checklist
      </Typography>

      {matchingResults.map(({ checklist, ...result }) => (
        <Checklist
          calculationResult={result}
          checklist={checklist}
          isDownloadAllowed={isDownloadAllowed}
          isLoading={isLoading}
          key={checklist.id}
          onDownloadChecklist={onDownloadChecklist}
        />
      ))}
    </Box>
  );
};

WorksheetChecklistPanel.propTypes = {
  checklistId: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default memo(WorksheetChecklistPanel);
